import { Box, CircularProgress } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Button } from "@src/appV2/redesign/components/Button";
import { Icon } from "@src/appV2/redesign/components/Icon";
import { ClipboardLogo } from "@src/appV2/redesign/components/Logos";
import { Text } from "@src/appV2/redesign/components/Text";
import loginIllustration from "@src/appV2/redesign/Login/assets/login-illustration.webp";
import { useIsRebrand } from "@src/appV2/redesign/theme/RebrandProvider";
import { berlinRebrandTheme, berlinTheme } from "@src/appV2/redesign/theme/theme";
import { type ReactNode } from "react";

type AuthState =
  | "checking-session"
  | "already-authenticated"
  | "confirm"
  | "signing-in"
  | "redirecting"
  | "error"
  | "invalid";

interface AuthRedirectViewRedesignedProps {
  authState: AuthState;
  error?: string;
  existingEmail?: string;
  magicLinkEmail?: string | null;
  /** Custom-scheme URL to open the native app. Shown on mobile browsers where Universal Links failed. */
  appSchemeUrl?: string;
  onContinue: () => void;
  onSignOutAndContinue: () => void;
  onGoToDashboard: () => void;
}

function AuthCard({
  showIllustration = true,
  children,
}: {
  showIllustration?: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "background.baseDark",
        paddingX: 5,
        paddingTop: "max(24px, env(safe-area-inset-top))",
        paddingBottom: "max(24px, env(safe-area-inset-bottom))",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          paddingX: { xs: 5, sm: 7 },
          paddingY: 7,
          backgroundColor: "background.standout",
          borderRadius: (theme) => theme.borderRadius?.large,
          boxShadow: (theme) => theme.shadow?.card,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
        }}
      >
        <ClipboardLogo height={60} width={180} variant="brown" />
        {showIllustration && (
          <Box component="img" src={loginIllustration} alt="" sx={{ height: 150, width: "auto" }} />
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 5,
            width: "100%",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function LoadingContent({ label }: { label: string }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, py: 6 }}>
      <CircularProgress />
      <Text variant="body1" sx={{ color: "base.base" }}>
        {label}
      </Text>
    </Box>
  );
}

function AlreadyAuthenticatedContent({
  existingEmail,
  magicLinkEmail,
  onGoToDashboard,
  onSignOutAndContinue,
}: Pick<
  AuthRedirectViewRedesignedProps,
  "existingEmail" | "magicLinkEmail" | "onGoToDashboard" | "onSignOutAndContinue"
>) {
  const isSameUser =
    existingEmail && magicLinkEmail && existingEmail.toLowerCase() === magicLinkEmail.toLowerCase();

  return (
    <>
      <Text variant="body1" sx={{ textAlign: "center", color: "base.base" }}>
        You are already signed in as{" "}
        <Text inline bold sx={{ color: "base.base" }}>
          {existingEmail}
        </Text>
        .
      </Text>
      <Button fullWidth variant="primary" size="large" onClick={onGoToDashboard}>
        Go to Dashboard
      </Button>
      <Button fullWidth variant="outlined" size="large" onClick={onSignOutAndContinue}>
        {isSameUser
          ? "Sign out and use this link"
          : `Sign in as ${magicLinkEmail ?? "a different user"}`}
      </Button>
    </>
  );
}

function ErrorContent({ authState, error }: { authState: "invalid" | "error"; error?: string }) {
  const message =
    authState === "invalid"
      ? "Invalid or missing authentication link. Please request a new login link."
      : error;
  const heading = authState === "invalid" ? "This link isn't valid" : "We couldn't sign you in";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 56,
            height: 56,
            borderRadius: "50%",
            backgroundColor: "background.error",
          }}
        >
          <Icon type="warning" sx={{ width: 28, height: 28, color: "base.danger" }} />
        </Box>
        <Text bold variant="h5" sx={{ textAlign: "center", color: "base.base" }}>
          {heading}
        </Text>
        <Text variant="body2" role="alert" sx={{ textAlign: "center", color: "base.muted" }}>
          {message}
        </Text>
      </Box>
      <Button fullWidth variant="primary" size="large" href={`${import.meta.env.BASE_URL}enter`}>
        Back to Login
      </Button>
    </>
  );
}

function ConfirmContent({
  appSchemeUrl,
  onContinue,
}: Pick<AuthRedirectViewRedesignedProps, "appSchemeUrl" | "onContinue">) {
  // Mobile browser where Universal Links didn't fire (e.g. Gmail/Outlook
  // embedded webview). Offer an explicit "Open in App" button that uses
  // the custom URL scheme, with a web-auth fallback.
  if (appSchemeUrl) {
    return (
      <>
        <Text variant="body1" sx={{ textAlign: "center", color: "base.base" }}>
          Tap below to finish signing in.
        </Text>
        <Button fullWidth variant="primary" size="large" href={appSchemeUrl}>
          Open in Clipboard App
        </Button>
        <Button fullWidth variant="outlined" size="large" onClick={onContinue}>
          Continue in browser
        </Button>
      </>
    );
  }

  return (
    <Button fullWidth variant="primary" size="large" onClick={onContinue}>
      Continue to Clipboard
    </Button>
  );
}

function renderContent(props: AuthRedirectViewRedesignedProps): ReactNode {
  switch (props.authState) {
    case "checking-session":
      return <LoadingContent label="Loading..." />;
    case "signing-in":
      return <LoadingContent label="Signing In..." />;
    case "redirecting":
      return <LoadingContent label="Redirecting..." />;
    case "already-authenticated":
      return <AlreadyAuthenticatedContent {...props} />;
    case "invalid":
    case "error":
      return <ErrorContent authState={props.authState} error={props.error} />;
    default:
      return <ConfirmContent {...props} />;
  }
}

export function AuthRedirectViewRedesigned({
  authState,
  error,
  existingEmail,
  magicLinkEmail,
  appSchemeUrl,
  onContinue,
  onSignOutAndContinue,
  onGoToDashboard,
}: AuthRedirectViewRedesignedProps) {
  const isRebrand = useIsRebrand();
  const theme = isRebrand ? berlinRebrandTheme : berlinTheme;

  const isLoadingState =
    authState === "checking-session" || authState === "signing-in" || authState === "redirecting";
  const isErrorState = authState === "invalid" || authState === "error";

  const content = renderContent({
    authState,
    error,
    existingEmail,
    magicLinkEmail,
    appSchemeUrl,
    onContinue,
    onSignOutAndContinue,
    onGoToDashboard,
  });

  return (
    <ThemeProvider theme={theme}>
      <AuthCard showIllustration={!isLoadingState && !isErrorState}>{content}</AuthCard>
    </ThemeProvider>
  );
}
